import type { MouseEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    useEventsExport,
    useHasUrlFilters,
    useOpenInNewWindow,
    useQueryExecution,
} from '@/hooks';
import { QueryResultSection } from './components/QueryResultSection';
import { lastQueryExportName } from './hooks/useQueryEventsExport';
import { EmptyResultsSuggestions } from './components/EmptyResultsSuggestions';
import { SimilarResults } from './components/SimilarResults';
import { QueryResultsLoadMore } from './components/QueryResultsLoadMore';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useQueryFiltersStore, useQueryResultsStore } from '@/stores';
import { usePipPlayerStore } from '@/stores/pip-player.store';
import { QueryResultsActionBar } from './components/QueryResultsActionBar';

export function QueryResults() {
    const { t } = useTranslation(I18nNamespace.QUERIES);
    const [searchParams] = useSearchParams();
    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);
    const resetFilters = useQueryFiltersStore((state) => state.resetFilters);
    const clearAlertRuleSelection = useQueryFiltersStore(
        (state) => state.clearAlertRuleSelection,
    );
    const clearObjectClassSelection = useQueryFiltersStore(
        (state) => state.clearObjectClassSelection,
    );
    const { scrollPosition, setScrollPosition, similarResults } =
        useQueryResultsStore();
    const {
        isLoading,
        isLoadingMore,
        hasMore,
        loadMore,
        query,
        events,
        displayedEvents,
        eventPageMap,
        currentPage,
    } = useQueryExecution();
    const hasAttachment = !!searchParams.get('attachment');
    const hasUrlFilters = useHasUrlFilters();
    const hasQuery = !!searchParams.get('q');

    const pipOpen = usePipPlayerStore((state) => state.open);
    const refreshPipNavigationContext = usePipPlayerStore(
        (state) => state.refreshNavigationContext,
    );
    const exitSelectionMode = useQueryResultsStore(
        (state) => state.exitSelectionMode,
    );
    const eventExport = useEventsExport({
        exportName: lastQueryExportName,
        onStarted: exitSelectionMode,
    });

    const pipItems = useMemo(
        () =>
            displayedEvents.map((scoredEvent) => ({
                id: scoredEvent.event.id,
            })),
        [displayedEvents],
    );

    useEffect(() => {
        refreshPipNavigationContext({
            type: 'query-results',
            items: pipItems,
            canLoadMore: hasMore,
            loadMore,
        });
    }, [pipItems, hasMore, loadMore, refreshPipNavigationContext]);

    const handleQuickView = (eventId: string) => {
        pipOpen({
            eventId,
            navigationContext: {
                type: 'query-results',
                items: pipItems,
                currentIndex: pipItems.findIndex((item) => item.id === eventId),
                canLoadMore: hasMore,
                loadMore,
            },
        });
    };

    const handleSimilarQuickView = (eventId: string) => {
        const items = similarResults.map(({ scoredEvent }) => ({
            id: scoredEvent.event.id,
        }));
        pipOpen({
            eventId,
            navigationContext: {
                type: 'similar-results',
                items,
                currentIndex: items.findIndex((item) => item.id === eventId),
            },
        });
    };

    const sections = useMemo(() => {
        const groups = displayedEvents.reduce((groupsByPage, scoredEvent) => {
            const page = eventPageMap.get(scoredEvent.event.id) ?? 0;
            const existing = groupsByPage.get(page) ?? [];
            groupsByPage.set(page, [...existing, scoredEvent]);
            return groupsByPage;
        }, new Map<number, typeof displayedEvents>());

        Array.from({ length: currentPage }, (_, index) => index + 1).forEach(
            (page) => {
                if (!groups.has(page)) groups.set(page, []);
            },
        );

        return Array.from(groups.entries())
            .sort(([pageA], [pageB]) => pageA - pageB)
            .map(([page, sectionEvents], index, allSections) => ({
                events: sectionEvents,
                isLatest: index === allSections.length - 1 && page > 0,
                key: page,
            }));
    }, [displayedEvents, eventPageMap, currentPage]);

    useEffect(() => {
        if (hasUrlFilters || hasQuery || hasAttachment) {
            return;
        }

        const { filters, selectedAlertRuleIds, selectedObjectClassIds } =
            useQueryFiltersStore.getState();
        const previousFilters = {
            ...filters,
            dateTime: { ...filters.dateTime },
            confidence: { ...filters.confidence },
        };
        const previousAlertRuleIds = new Set(selectedAlertRuleIds);
        const previousObjectClassIds = new Set(selectedObjectClassIds);

        resetFilters();
        clearAlertRuleSelection();
        clearObjectClassSelection();

        return () => {
            useQueryFiltersStore.setState({
                filters: previousFilters,
                selectedAlertRuleIds: previousAlertRuleIds,
                selectedObjectClassIds: previousObjectClassIds,
            });
        };
    }, [
        resetFilters,
        clearAlertRuleSelection,
        clearObjectClassSelection,
        hasUrlFilters,
        hasQuery,
        hasAttachment,
    ]);

    const shouldScrollToNewSectionRef = useRef(false);
    const lastSectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (
            shouldScrollToNewSectionRef.current &&
            lastSectionRef.current &&
            scrollContainer
        ) {
            shouldScrollToNewSectionRef.current = false;
            const containerRect = scrollContainer.getBoundingClientRect();
            const sectionRect = lastSectionRef.current.getBoundingClientRect();
            const topPadding = 240;
            const offset =
                sectionRect.top -
                containerRect.top +
                scrollContainer.scrollTop -
                topPadding;
            scrollContainer.scrollTo({
                top: Math.max(0, offset),
                behavior: 'smooth',
            });
        }
    }, [sections, scrollContainer]);

    useEffect(() => {
        if (scrollContainer && scrollPosition > 0 && events.length > 0) {
            scrollContainer.scrollTo(0, scrollPosition);
        }
    }, [scrollContainer, scrollPosition, events.length]);

    const { openEvent } = useOpenInNewWindow();

    const handleEventClick = (eventId: string, event: MouseEvent) => {
        if (scrollContainer) {
            setScrollPosition(scrollContainer.scrollTop);
        }
        openEvent(eventId, event, {
            state: {
                from: 'query-results',
                feedContext: { type: 'queryResults', eventId },
            },
        });
    };

    if ((!query || !query.trim()) && !hasAttachment) {
        return (
            <div className="h-full flex items-center justify-center">
                <p className="text-grey-300">{t('emptyStates.noQuery')}</p>
            </div>
        );
    }

    const isInitialLoading = isLoading && !isLoadingMore;

    return (
        <div className="h-full flex flex-col relative">
            <QueryResultsActionBar
                scrollContainer={scrollContainer}
                visibleEventIds={displayedEvents.map(
                    (scoredEvent) => scoredEvent.event.id,
                )}
                eventExport={eventExport}
            />

            <div
                ref={setScrollContainer}
                className="w-full flex flex-col flex-1 min-h-0 overflow-y-auto scrollbar-hide relative z-0"
            >
                <div className="w-full max-w-content mx-auto flex flex-col pt-24 gap-1">
                    {sections.length === 0 ? (
                        <>
                            <QueryResultSection
                                events={[]}
                                isLoading={isInitialLoading}
                                query={query}
                                hasAttachment={hasAttachment}
                                onEventClick={handleEventClick}
                                onQuickView={handleQuickView}
                                eventExport={eventExport}
                            />
                            {!isInitialLoading && <EmptyResultsSuggestions />}
                        </>
                    ) : (
                        sections.map((section, sectionIndex) => (
                            <div
                                key={section.key}
                                ref={
                                    sectionIndex === sections.length - 1
                                        ? lastSectionRef
                                        : undefined
                                }
                                className={
                                    sectionIndex === sections.length - 1 &&
                                    sections.length > 1
                                        ? 'min-h-screen'
                                        : undefined
                                }
                            >
                                <QueryResultSection
                                    events={section.events}
                                    isLoading={
                                        sectionIndex === 0
                                            ? isInitialLoading
                                            : section.isLatest && isLoadingMore
                                    }
                                    additionalResults={sectionIndex > 0}
                                    query={query}
                                    hasAttachment={hasAttachment}
                                    onEventClick={handleEventClick}
                                    onQuickView={handleQuickView}
                                    eventExport={eventExport}
                                />
                            </div>
                        ))
                    )}

                    {!isInitialLoading && (
                        <SimilarResults
                            results={similarResults}
                            onEventClick={handleEventClick}
                            onQuickView={handleSimilarQuickView}
                        />
                    )}

                    {events.length > 0 && !isLoading && (
                        <div className="flex justify-center items-center h-12 mt-4">
                            <QueryResultsLoadMore
                                isLoading={false}
                                isLoadingMore={isLoadingMore}
                                hasMore={hasMore}
                                onLoadMore={() => {
                                    shouldScrollToNewSectionRef.current = true;
                                    loadMore();
                                }}
                            />
                        </div>
                    )}

                    <div className="pb-36" />
                </div>
            </div>

            <div className="absolute bottom-0 left-0 right-0 h-64 pointer-events-none bg-gradient-to-t from-grey-700 to-transparent" />
        </div>
    );
}
